import React from 'react';
import PropTypes from 'prop-types';
import { PAGE_TYPES } from '@sparefoot/tracking-events';
import { compose, withPropsOnChange } from 'recompose';
import {
	get, isUndefined, split, join, upperFirst, toUpper, capitalize
} from 'lodash';

import { connect } from 'react-redux';
import { portableStoragePSPCLPSelector } from 'store/selectors/landing';
import { withPageViewTracking } from 'components/core/library/tracking-utils';
import { withSearchHandler } from 'decorators';

import { LandingHero } from 'components/landing/LandingHero/LandingHero';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Image } from 'components/core/Image';
import { Carousel } from 'components/core/Carousel';
import { Page } from 'components/core/Page';
import { StorageQuestions } from 'components/landing/StorageQuestions';
import { PortableStoragePromoCards } from 'components/brand/PortableStoragePromoCards';
import { PortableStorageComparison } from 'components/brand/PortableStorageComparison';
import { PortableStorageHowItWorks } from 'components/landing/PortableStorageHowItWorks';
import { LandingSection } from 'components/landing/LandingSection';
import { H3, H4 } from 'components/core/Headline';
import { Paragraph } from 'components/core/Paragraph';
import { Quote } from 'components/core/Quote';
import { scrollToElement } from 'lib/helpers';
import { PortableStorageCities } from 'components/landing/PortableStorageCities';
import { DrillDownLinks } from 'components/content/DrillDownLinks';
import { Breadcrumbs, Crumb } from 'components/core/Breadcrumbs';
import { StickySearchBar } from 'components/landing/StickySearchBar';


import podsUnit from 'assets/images/brand/portable-storage/unit-pods.jpg';
import packRat from 'assets/images/brand/portable-storage/pack-rat.png';
import uPackUnit from 'assets/images/brand/portable-storage/unit-relocube.jpg';

import './portableStoragePage.scss';

export const enhance = compose(
	connect(portableStoragePSPCLPSelector),
	withPageViewTracking(PAGE_TYPES.PSP),
	withSearchHandler,
	withPropsOnChange(['match'], ({ match }) => {
		const isPSP = isUndefined(get(match, 'params.city'));
		const cityString = isPSP ? '' : `${join(split(match.params.city, '-').map((s) => upperFirst(s)), ' ')}`;
		const cityStateURL = isPSP ? '' : `/portable-storage/${match.params.city}-${match.params.state}.html`;
		const crumbs = [];
		crumbs.push(
			<Crumb
				key="portable-storage"
				isLast={isPSP}
				href="/portable-storage.html"
				label="Portable Storage"
			/>
		);
		if (!isPSP) {
			crumbs.push(
				<Crumb
					key="portable-storage-city"
					isLast
					href={cityStateURL}
					label={cityString}
				/>
			);
		}
		return {
			isPSP,
			crumbs,
			city: isPSP ? '' : join(split(match.params.city, '-').map((s) => upperFirst(s)), ' '),
			state: isPSP ? '' : toUpper(match.params.state)
		};
	})
);


export function PortableStoragePage({
	crumbs,
	isPSP,
	content,
	location,
	city,
	state, 
	handleSearch
}) {
	return (
		<Page className="portable-storage-page psp-landing-page" pageType={PAGE_TYPES.PORTABLE_STORAGE_PSP} landingPage>
			<LandingHero
				dark
				withSearchBox={false}
				headline={`Portable Storage ${isPSP ? '' : `in ${city}, ${state}`}`}
				subheading="Storage that comes to you. Moving? You can also have your things delivered to your new home! It's the most convenient way to move and store your stuff."
			>
				<Button
					onClick={() => scrollToElement('#PromoCards')}
					rel="noopener noreferrer"
				>
					Get Local Deals
				</Button>
			</LandingHero>
			<StickySearchBar handleSearch={handleSearch} />
			<StorageQuestions />
			<div id="HowItWorks" />
			<div className="section-content how-it-works-container">
				<PortableStorageHowItWorks isPSP={isPSP} city={city} state={state} />
			</div>
			<div id="PromoCards">
				<PortableStoragePromoCards isPSP={isPSP} city={city} state={state} />
			</div>
			<div className="carousel-container">
				<Carousel >
					<div className="pods-testimonial">
						<div className="testimonial">
							<Quote
								className="static-testimonial-quote"
							quote="The PODS interaction was terrific. Excellent communication, very respectful and timely. We moved cross-country and had to store our belongings for 6 months while our new home was under construction. Everything arrived in perfect condition and PODS also offered referrals for moving help, which was great!!"
								source="Marlene K., Cleveland, OH"
							/>
							<div className="image-wrapper">
								<Image
									src={podsUnit}
									className="static-testimonial-image"
									alt="podsUnit"
								/>
							</div>
						</div>
					</div>
					<div className="packrat-testimonial">
						<div className="testimonial">
							<Quote
								className="static-testimonial-quote"
							quote="A+ Service. It's very rare that I deal with a company that delivers on everything it says but this is one. If you're moving I highly recommend this place. They arrived on time and had my stuff moved from across the country much sooner than I expected and delivered everything on schedule."
								source="Bobby D., CA"
							/>
							<div className="image-wrapper">
								<Image
									src={packRat}
									className="static-testimonial-image"
									alt="packrat unitg"
								/>
							</div>
						</div>
					</div>
					<div className="uPack-testimonial">
						<div className="testimonial">
							<Quote
								className="static-testimonial-quote"
							quote="Amazing. I will never use another moving company! I will highly recommend U-Pack to anyone who asks. Could not have had a better, easier, faster, no hassle moving experience. Totally worth the price for the pickup/drop off. Thank you guys all so much!"
								source="Fae G., Englewood, CO"
							/>
							<div className="image-wrapper">
								<Image
									src={uPackUnit}
									className="static-testimonial-image"
									alt="uPack Unit"
								/>
							</div>
						</div>
					</div>
				</Carousel>
			</div>
			<PortableStorageComparison isPSP={isPSP} city={city} />
			<div className="section-content static-content">
				<LandingSection>
					<H3>
						{`Everything you need to know about portable storage ${isPSP ? '' : `in ${city}, ${state}`}`}
					</H3>
					<H4>
						{`Portable storage for moving ${isPSP ? '' : `in ${city}`}`}
					</H4>
					<Paragraph>
						One of the most common reasons people need storage is that they're moving to a new home.
						Portable storage is a safe and convenient solution for getting all of your stuff where it needs
						to go - especially if you have to deal with the weird in-between stage of not having anywhere to
						put it. The best part of using portable storage for moving is that the company can then deliver
						your unit to your new home!
					</Paragraph>
					<Paragraph>
						Some moves can be tricky. For example, your new home might not be ready when you move out of
						your old home. Portable storage is an excellent storage option for these situations. Whatever
						your specific moving logistics are, portable storage can come to the rescue!
					</Paragraph>
				</LandingSection>
				<LandingSection className="landing-header-space">
					<H3>
						Learn more about portable storage in our helpful resources.
					</H3>
					<H4>
						Packing a Portable Storage Container for a Long-Distance Move? We've Got You Covered
					</H4>
					<Paragraph>
						If you're packing for a long-distance move, you may have been considering using a storage
						container such as a PODS container, rather than renting a truck or hiring professional movers.
					</Paragraph>
					<Link
						href="/blog/19630-how-to-pack-pods-for-long-distance-move/"
						target="_blank"
						rel="noreferrer noopener"
					>
						Read More
					</Link>
					<H4>
						Rental Trucks vs. Portable Storage Containers
					</H4>
					<Paragraph>
						If you want to avoid the hassle of loading a rental truck and driving it to your new home,
						there's another moving option to consider: portable storage containers. In addition to offering
						storage services, portable container companies will deliver units directly to your new home,
						said Kyle Taylor, Vice President of the MI-BOX of San Diego.
					</Paragraph>
					<Link
						href="/blog/19660-rental-truck-vs-moving-pods/"
						target="_blank"
						rel="noreferrer noopener"
					>
						Read More
					</Link>
					<H4>
						10 Things to Know About Portable Storage Containers Before You Book
					</H4>
					<Paragraph>
						Using a portable storage container to transport your belongings to a new home may sound
						appealing, but there are things you need to understand before you book a unit.
					</Paragraph>
					<Link
						href="/blog/19755-10-things-to-know-about-portable-storage-containers-before-you-book/" // eslint-disable-line max-len
						target="_blank"
						rel="noreferrer noopener"
					>
						Read More
					</Link>
				</LandingSection>
				{isPSP &&
					<PortableStorageCities />
				}
				{(!isPSP && content.drillDownLinks && content.drillDownLinks.vehicleStorage) &&
					<div className="portable-storage-content">
						<DrillDownLinks
							headline={`Browse ${capitalize(location.city)} Storage by Type`}
							links={content.drillDownLinks.vehicleStorage}
						/>
					</div>
				}
			</div>

			<div className="section-content static-content content-block">
				<Breadcrumbs
					crumbs={crumbs}
				/>
			</div>
		</Page>
	);
}

PortableStoragePage.propTypes = {
	crumbs: PropTypes.arrayOf(PropTypes.object),
	isPSP: PropTypes.bool,
	content: PropTypes.object,
	location: PropTypes.object,
	city: PropTypes.string,
	state: PropTypes.string,
	handleSearch: PropTypes.func
};

PortableStoragePage.defaultProps = {
	crumbs: []
};

export default enhance(PortableStoragePage);
